import {RawParams, StateOrName, StateService, TransitionOptions, UIRouterGlobals, UrlService} from '@uirouter/angular';
import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {STATE_NAMES} from '../common/state-names';

@Injectable({ providedIn: 'root' })
export class RoutingService {

    constructor(private stateService: StateService, private urlService: UrlService,
         private routerGlobals: UIRouterGlobals) {
    }

    get params(): RawParams {
        return this.routerGlobals.params || {};
    }

    get name(): string {
        return this.routerGlobals.current.name;
    }

    /**
     * The active state and its ancestors, most specific first.
     *
     * A state registered with an explicit `parent` keeps its bare leaf name (UI-Router's name
     * builder returns the name as declared), so `companyARInvoices` gives no textual hint that it
     * lives under `app.accountReceivables`. Callers that need to reason about the branch a page
     * belongs to must walk the router's own state tree rather than parse the name.
     */
    get stateChain(): string[] {
        const path = this.routerGlobals.$current?.path || [];
        return path
            .map((state) => state.name)
            .filter((name) => !!name)
            .reverse();
    }

    /**
     * Navigates to `to`, returning the transition promise so callers that need to react to the
     * outcome can (most don't — the promise is safe to ignore).
     *
     * Pass `{source: 'sref'}` in `options` for navigation triggered by a user action. UI Router
     * defaults `source` to `'unknown'`, which the company-switch guard treats as a programmatic
     * self-navigation and aborts; marking real clicks keeps them from being silently dropped while a
     * company switch is in progress. See FeatureFlagRouteGuardService.
     */
    go(to: StateOrName, rawParams?: RawParams, reload = false, options?: TransitionOptions): Promise<any> {
        return this.stateService.go(to, rawParams, {reload: reload, ...options});
    }

    /**
     * Does nothing until the router has completed its first transition. A stale session is cleared by
     * AuthenticationService's constructor while the initial URL sync is still in flight, and for a
     * protected URL the auth guard in main.routes redirects that sync to login itself. A second login
     * navigation started from the root state raced it, both succeeded, and the first one's view config
     * was never removed — it kept the root <ui-view> on Sign in after the next login, while the URL
     * moved on to the app.
     */
    goToLoginPage() {
        if (!this.routerGlobals.successfulTransitions.peekTail()) {
            return;
        }
        if (this.routerGlobals.current.name !== STATE_NAMES.login && !environment.isMaintenance) {
            switch (this.routerGlobals.current.name) {
                case 'sign-up':
                    this.stateService.go(STATE_NAMES.signUp);
                    break;
                case 'restore-password':
                    this.stateService.go(STATE_NAMES.restorePassword);
                    break;
                case 'restore-email':
                    this.stateService.go(STATE_NAMES.restoreEmail);
                    break;
                case 'user-activate':
                    this.stateService.go(STATE_NAMES.userActivate);
                    break;
                case 'new-password':
                    this.stateService.go(STATE_NAMES.newPassword);
                    break;
                case 'activate':
                    this.stateService.go(STATE_NAMES.activate);
                    break;
                default:
                    this.stateService.go(STATE_NAMES.login);
                    break;
            }
        } else if (environment.isMaintenance) {
            this.stateService.go(STATE_NAMES.home);
        }
    }

    queryString(key: string): string {
        return this.urlService.parts().search[key];
    }
}
